import {
  useState,
  type ComponentPropsWithoutRef,
  memo,
  useRef,
  useCallback,
} from 'react';
import {
  RichTextEditor,
  RichTextEditorToolbarSticky,
} from '@/components/RichTextEditor';
import type { Editor } from '@tiptap/core';
import { Button } from '@simplywallst/ui-core';
import { useFlag } from '@/features/unleash';
import type { Content } from '@/components/RichTextEditor/RichTextEditor';
import loadable from '@loadable/component';

const NarrativeEditorSuggestions = loadable(
  () => import('./components/NarrativeEditorSuggestions'),
  {
    resolveComponent: (component) => component.NarrativeEditorSuggestions,
  }
);

interface Props
  extends Pick<ComponentPropsWithoutRef<typeof RichTextEditor>, 'onUpdate'> {
  errorMessage?: string;
  onDimissAlert?: () => void;
  defaultContent?: string;
}

const PublicNarrativeRichTextEditorBase = ({
  onUpdate,
  defaultContent,
}: Props) => {
  const flag = useFlag({ name: 'mono-narrative-editor-suggestions' });
  const areSuggestionsEnabled = flag.flagsReady && flag.enabled;

  const [editor, setEditor] = useState<Editor | null>(null);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const editorContainerRef = useRef<HTMLDivElement>(null);
  const previousHeightRef = useRef<number | null>(null);

  const handleUpdate = useCallback(
    (content: Content) => {
      onUpdate?.(content);
      const newHeight = editorContainerRef.current?.offsetHeight ?? 0;
      const previousHeight = previousHeightRef.current ?? 0;
      previousHeightRef.current = newHeight;

      if (!previousHeight || previousHeight === newHeight) return;
      if (!newHeight || !editor) return;
      const { state } = editor;
      const { selection, doc } = state;
      const { from } = selection;
      const lastNode = doc.lastChild;
      const isInLastNode = lastNode && doc.resolve(from).node() === lastNode;

      if (!isInLastNode) return;

      /**
       * Scroll to the bottom of the editor when the content is updated when a user is editting the last node.
       * This is needed since the toolbar is sticky and the editor is not scrollable,
       * new content would hide behind the sticky toolbar as we type.
       */
      window.scrollTo(0, document.body.scrollHeight);
    },
    [editor, onUpdate]
  );

  return (
    <div
      className="relative min-h-[calc(100dvh-320px)]"
      data-testid="public-narrative-editor"
      data-initialised={Boolean(editor)}
      ref={editorContainerRef}
    >
      <div className="grid h-full bg-p-grey-100 px-x3 pt-x3 lg:rounded-x2">
        <RichTextEditor
          defaultContent={defaultContent}
          onUpdate={handleUpdate}
          enableImages
          onInit={(_conent, editorInstance) => setEditor(editorInstance)}
          placeholderText={
            <div
              className="grid grid-cols-[max-content] grid-rows-[auto_auto] items-center gap-x1 md:grid-cols-[max-content_max-content] md:grid-rows-[auto]"
              role="note"
            >
              <span className="text-base text-soft">
                What&apos;s the story behind this stock?
              </span>
              {areSuggestionsEnabled && (
                <Button
                  styleType="gentle"
                  onClick={() => setShowSuggestions((isOpen) => !isOpen)}
                >
                  💡 Get Ideas for questions to ask yourself
                </Button>
              )}
            </div>
          }
        >
          <RichTextEditorToolbarSticky className="bottom-x7 lg:bottom-0">
            {areSuggestionsEnabled && !!editor && (
              <NarrativeEditorSuggestions
                editor={editor}
                onOpen={() => setShowSuggestions(true)}
                onClose={() => setShowSuggestions(false)}
                isOpen={showSuggestions}
              />
            )}
          </RichTextEditorToolbarSticky>
        </RichTextEditor>
      </div>
    </div>
  );
};

export const PublicNarrativeRichTextEditor = memo(
  PublicNarrativeRichTextEditorBase
);
